/**
 * ⓘ — the one place in the app where a data source is allowed to name itself.
 *
 * The rule this component exists to enforce: a pilot filling in the form never
 * reads a model name, a licence code, an endpoint or the word „Quelle“. What
 * they see is the value and how old it is. Who published it, how coarse it is
 * and what it must not be used for lives one tap away, in two to four sentences,
 * with the link the licence asks for and a way to the full page.
 *
 * It is deliberately not a tooltip: the text is long enough to read, it has
 * links in it, and it has to work on a phone. It is deliberately not a dialog
 * either — nothing here is a decision, so nothing here may take the screen.
 *
 * Clicks are swallowed because the popover is used inside a `<summary>`: without
 * `preventDefault()` opening the ⓘ — or reading the panel it opens — would fold
 * the section it belongs to (see {@link swallowBody}).
 */
import { IconInfo } from '../app/icons';
import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../i18n';

/** A link a licence asks for. The text is a proper noun — nothing to translate. */
export interface InfoLink {
  href: string;
  text: string;
}

export function InfoPopover({
  body,
  extra,
  links = [],
}: {
  /** the two-to-four sentences: who, how fresh, what it is not */
  body: string;
  /** one more line where the answer has published limits worth naming */
  extra?: string | undefined;
  links?: readonly InfoLink[];
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const bodyRef = useRef<HTMLSpanElement>(null);

  /**
   * The ⓘ sits wherever its section's header puts it — right-aligned in a wide
   * form, hard left in a narrow pane, halfway across in a map panel heading — so
   * no single CSS anchor keeps the panel on screen. Measure once on open and
   * slide it back inside; nothing here reflows anything else, because the panel
   * is absolutely positioned.
   */
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!open || !el) return;
    el.style.transform = '';
    const r = el.getBoundingClientRect();
    const pad = 8;
    const dx = r.left < pad ? pad - r.left : r.right > window.innerWidth - pad ? window.innerWidth - pad - r.right : 0;
    if (dx) el.style.transform = 'translateX(' + Math.round(dx) + 'px)';
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const swallow = (e: ReactMouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * The same for the panel itself — and `stopPropagation()` alone is not enough.
   *
   * The popover lives inside a `<summary>`, and a click on a plain `<span>` has
   * the nearest ancestor with activation behaviour as its activation TARGET: the
   * summary. Stopping propagation does not set the canceled flag, so reading the
   * two sentences folded the section away underneath them. Only the button
   * escaped it, by being an activation target itself.
   *
   * A click on a link is left alone: cancelling it would stop the link opening.
   */
  const swallowBody = (e: ReactMouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const el = e.target instanceof Element ? e.target : null;
    if (!el?.closest('a')) e.preventDefault();
  };

  return (
    <span className="infopop" ref={ref}>
      <button
        type="button"
        className="freshbtn"
        title={t('fresh.info')}
        aria-label={t('fresh.info')}
        aria-expanded={open}
        onClick={e => {
          swallow(e);
          setOpen(v => !v);
        }}
      >
        <IconInfo />
      </button>
      {open ? (
        <span className="infobody" ref={bodyRef} onClick={swallowBody}>
          <span className="infotext">{body}</span>
          {extra ? <span className="infotext dim">{extra}</span> : null}
          {links.length ? (
            <span className="infolinks">
              {links.map(l => (
                <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer">
                  {l.text}
                </a>
              ))}
            </span>
          ) : null}
          <Link className="infomore" to="/sources" onClick={() => setOpen(false)}>
            {t('src.more')}
          </Link>
        </span>
      ) : null}
    </span>
  );
}
